/* Yarn & global trade — the one payload the page is drawn from.
 *
 * The shape is the design's `window.TRADE`, unchanged, because the page's
 * arithmetic is a port of the design's own script: the server assembles the
 * series and the page does the trailing-twelve-month sums, unit values and
 * rankings exactly as the design does. */

import { post, request } from './api'

/** One period of one flow. `kg` is null where a filed net weight was so far
 *  out of line with its neighbours that it was dropped; `uw` is the value
 *  that goes with the weight kept, for unit-value sums. */
export interface TradeCell { kg: number | null; usd: number; uw?: number }

export type Flow = 'X' | 'M'
type Series = Record<string, Partial<Record<Flow, Record<string, TradeCell>>>>

export interface TradeRow { code: number; name: string; kg: number; usd: number }

export interface TradePayload {
  built: string
  /** Last month of India's own export returns, YYYYMM. */
  latestMonth: string
  /** Last month of mirror (import) data, YYYYMM. */
  mirrorLast: string
  droppedWeights: number
  /** `codes` are the HS headings in the group — what tells cotton from yarn. */
  groups: { key: string; label: string; hs: string; codes: string[]; name?: string }[]
  annual: Series
  monthly: Series
  /** ["2026M06", USD per kg] — the World Bank Cotton A Index. */
  cottonIndex: [string, number][]
  world: Record<string, Record<string, Partial<Record<Flow, TradeRow[]>>>>
  worldMeta: Record<string, Record<string, Partial<Record<Flow, number>>>>
  partners: Partial<Record<Flow, Record<string, Record<string, TradeRow[]>>>>
}

/** One quoted price series — the design's `window.YARN_PRICES.series[i]`.
 *  `count` is the feed's own label ("40/1 Carded", "OE 16/1", "50s Combed
 *  Compact"); the page reads Ne, ply and process out of it. `unit` is the
 *  quote's "INR/kg" style unit and `freq` the feed, Daily or Weekly. */
export interface YarnPriceSeries {
  key: string
  country: string
  market: string
  product: string
  count: string
  unit: string
  freq: string
  /** [ISO date, price], oldest first. */
  pts: [string, number][]
}

export interface YarnPrices {
  /** The latest quote date on file, or null when nothing is loaded. */
  updated: string | null
  series: YarnPriceSeries[]
}

export const trade = {
  payload: () => request<TradePayload>('/trade/payload'),
  reload: () => post<{ ok: boolean }>('/trade/reload'),
  yarnPrices: () => request<YarnPrices>('/trade/yarn-prices'),
  /** Adds the price files (daily list, weekly grid or the dashboard's JSON)
   *  to the series on file; a day already quoted is overwritten. */
  uploadYarnPrices: async (files: File[]) => {
    const fd = new FormData()
    files.forEach((f) => fd.append('files', f))
    return request<{ ok: boolean; series: number; kinds: string[]; rows: number; warnings: string[] }>(
      '/trade/yarn-prices', { method: 'POST', body: fd })
  },
}
